// src/screens/academique/CourseViewer.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
  Alert,
  AlertTitle,
  Snackbar,
  Link
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Slideshow as PptIcon,
  VideoLibrary as VideoIcon,
  Description as DescriptionIcon,
  Error as ErrorIcon,
  FilePresent as FilePresentIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import FormationService from '../../services/FormationService';
import CoursService from '../../services/CoursService';

const CourseViewer = ({ open, course, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [docHtmlContent, setDocHtmlContent] = useState('');
  const videoRef = useRef(null);
  
  // État pour la notification de téléchargement
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (open && course) {
      setDocHtmlContent('');
      loadCourseContent();
    }
    return () => {
      revokeBlobUrl();
    };
  }, [open, course]);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  /**
   * Enregistrer l'historique de téléchargement
   * @param {number} courseId - ID du cours
   */
  const recordDownloadHistory = async (courseId) => {
    try {
      const historyData = {
        cours_id: courseId,
        action: 'Download'
      };
      const result = await CoursService.createHistory(historyData);
      if (result && result.status === true) {
        setSnackbar({
          open: true,
          message: `Téléchargement de "${course?.title || 'le cours'}" enregistré`,
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'enregistrement du téléchargement',
        severity: 'error'
      });
    }
  };

  const getMimeType = (extension) => {
    const mimeTypes = {
      pdf:  'application/pdf',
      ppt:  'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      doc:  'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      mp4:  'video/mp4',
      avi:  'video/x-msvideo',
      mov:  'video/quicktime',
      mkv:  'video/x-matroska',
      webm: 'video/webm',
      wmv:  'video/x-ms-wmv',
      jpg:  'image/jpeg',
      jpeg: 'image/jpeg',
      png:  'image/png',
      gif:  'image/gif',
    };
    return mimeTypes[extension.toLowerCase()] || 'application/octet-stream';
  };

  const getFileType = (extension) => {
    const ext = extension.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (ext === 'ppt' || ext === 'pptx') return 'ppt';
    if (['mp4', 'avi', 'mov', 'mkv', 'webm', 'wmv'].includes(ext)) return 'video';
    if (ext === 'doc' || ext === 'docx') return 'doc';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    return 'other';
  };

  // Fonction pour vérifier si le navigateur supporte le format vidéo
  const isVideoFormatSupported = (extension) => {
    const ext = extension.toLowerCase();
    const video = document.createElement('video');
    
    // Formats supportés par la plupart des navigateurs modernes
    const supportedFormats = {
      mp4: video.canPlayType('video/mp4'),
      webm: video.canPlayType('video/webm'),
      ogg: video.canPlayType('video/ogg'),
      mov: video.canPlayType('video/quicktime'),
      avi: video.canPlayType('video/x-msvideo'),
      mkv: video.canPlayType('video/x-matroska'),
      wmv: video.canPlayType('video/x-ms-wmv')
    };
    
    return supportedFormats[ext] !== '';
  };

  const base64ToArrayBuffer = (base64) => {
    const cleanBase64 = base64.replace(/\s/g, '');
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const base64ToBlobUrl = (base64, mimeType) => {
    try {
      const arrayBuffer = base64ToArrayBuffer(base64);
      const blob = new Blob([arrayBuffer], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (err) {
      return null;
    }
  };

  /**
   * Charge mammoth.js depuis CDN si nécessaire puis convertit le DOCX en HTML.
   */
  const convertDocxToHtml = async (base64Content) => {
    try {
      if (!window.mammoth) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const arrayBuffer = base64ToArrayBuffer(base64Content);
      const result = await window.mammoth.convertToHtml({ arrayBuffer });
      return result.value;
    } catch (err) {
      return null;
    }
  };

  const loadCourseContent = async () => {
    setLoading(true);
    setError(false);
    setErrorMessage('');
    revokeBlobUrl();

    try {
      const result = await FormationService.getCoursById(course.id);

      if (result && result.status === true) {
        const courseData = result.data;

        const fileName      = courseData.file_name || courseData.title || '';
        const fileExtension = fileName.split('.').pop().toLowerCase();
        const fileType      = getFileType(fileExtension);
        const mimeType      = getMimeType(fileExtension);
        const base64Content = courseData.file_content || null;
        const rawUrl        = courseData.file_url || courseData.drive_link || '';

        if (!base64Content && !rawUrl) {
          setError(true);
          setErrorMessage('Aucun fichier associé à ce cours');
          setLoading(false);
          return;
        }

        // ────────────────────────────────────────────────
        // PDF  →  <object data="blob:..."> affiché inline
        // ────────────────────────────────────────────────
        if (fileType === 'pdf') {
          let displayUrl = rawUrl;
          if (base64Content) {
            const blobUrl = base64ToBlobUrl(base64Content, mimeType);
            if (blobUrl) {
              blobUrlRef.current = blobUrl;
              displayUrl = blobUrl;
            }
          }
          setFileInfo({
            type: fileType, name: fileName, mimeType,
            displayUrl, downloadUrl: displayUrl, hasBase64: !!base64Content,
          });
          setLoading(false);
          return;
        }

        // ────────────────────────────────────────────────
        // DOCX → mammoth convertit en HTML puis iframe srcdoc
        // ────────────────────────────────────────────────
        if (fileType === 'doc') {
          if (base64Content) {
            const html = await convertDocxToHtml(base64Content);
            const dlUrl = base64ToBlobUrl(base64Content, mimeType);
            if (dlUrl) blobUrlRef.current = dlUrl;

            if (html) {
              setDocHtmlContent(html);
              setFileInfo({
                type: fileType, name: fileName, mimeType,
                displayUrl: 'srcdoc', downloadUrl: dlUrl, hasBase64: true,
              });
            } else {
              setFileInfo({
                type: fileType, name: fileName, mimeType,
                displayUrl: null, downloadUrl: dlUrl, hasBase64: true,
              });
              setError(true);
              setErrorMessage("Impossible de convertir le document Word pour l'affichage. Vous pouvez le télécharger.");
            }
          } else {
            const isLocal = rawUrl.includes('localhost') || rawUrl.includes('127.0.0.1');
            setFileInfo({
              type: fileType, name: fileName, mimeType,
              displayUrl: isLocal ? null : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`,
              downloadUrl: rawUrl, hasBase64: false,
            });
            if (isLocal) {
              setError(true);
              setErrorMessage('Affichage impossible en local sans contenu base64. Téléchargez le fichier.');
            }
          }
          setLoading(false);
          return;
        }

        // ────────────────────────────────────────────────
        // PPT/PPTX → pas de rendu JS client, téléchargement seulement en local
        // ────────────────────────────────────────────────
        if (fileType === 'ppt') {
          const isLocal = rawUrl.includes('localhost') || rawUrl.includes('127.0.0.1');
          let dlUrl = rawUrl;
          if (base64Content) {
            const blobUrl = base64ToBlobUrl(base64Content, mimeType);
            if (blobUrl) { blobUrlRef.current = blobUrl; dlUrl = blobUrl; }
          }
          const officeUrl = (!isLocal && !base64Content)
            ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`
            : null;
          setFileInfo({
            type: fileType, name: fileName, mimeType,
            displayUrl: officeUrl, downloadUrl: dlUrl, hasBase64: !!base64Content,
          });
          if (!officeUrl) {
            setError(true);
            setErrorMessage(
              "L'affichage des fichiers PowerPoint en local n'est pas supporté par le navigateur. " +
              "Téléchargez le fichier pour l'ouvrir avec PowerPoint ou LibreOffice."
            );
          }
          setLoading(false);
          return;
        }

        // ────────────────────────────────────────────────
        // Vidéo / Image
        // ────────────────────────────────────────────────
        let displayUrl = rawUrl;
        if (base64Content) {
          const blobUrl = base64ToBlobUrl(base64Content, mimeType);
          if (blobUrl) { blobUrlRef.current = blobUrl; displayUrl = blobUrl; }
        }
        
        // Vérifier si le format vidéo est supporté
        const isSupported = isVideoFormatSupported(fileExtension);
        
        setFileInfo({
          type: fileType, 
          name: fileName, 
          mimeType,
          displayUrl, 
          downloadUrl: displayUrl, 
          hasBase64: !!base64Content,
          isFormatSupported: isSupported,
          extension: fileExtension
        });
        
        // Pour WMV, afficher un message d'avertissement si non supporté
        if (fileType === 'video' && !isSupported && fileExtension === 'wmv') {
          setError(true);
          setErrorMessage(
            "Le format vidéo WMV n'est pas supporté par votre navigateur pour la lecture en ligne.\n\n" +
            "Solutions possibles :\n" +
            "1. Téléchargez la vidéo et lisez-la avec un lecteur compatible (VLC, Windows Media Player)\n" +
            "2. Utilisez un navigateur comme Edge ou Safari qui supporte mieux ce format\n" +
            "3. Contactez l'administrateur pour convertir la vidéo en MP4"
          );
        }
        
        setLoading(false);

      } else {
        setError(true);
        setErrorMessage(result?.message || 'Erreur lors du chargement du cours');
        setLoading(false);
      }
    } catch (err) {
      setError(true);
      setErrorMessage('Impossible de charger le contenu du cours');
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':   return <PdfIcon sx={{ color: '#D32F2F', fontSize: 40 }} />;
      case 'ppt':   return <PptIcon sx={{ color: '#ED6C02', fontSize: 40 }} />;
      case 'video': return <VideoIcon sx={{ color: '#2E7D32', fontSize: 40 }} />;
      case 'doc':   return <DescriptionIcon sx={{ color: '#1976D2', fontSize: 40 }} />;
      default:      return <FilePresentIcon sx={{ color: '#1976D2', fontSize: 40 }} />;
    }
  };

  const getFileTypeLabel = (fileType) => {
    switch (fileType) {
      case 'pdf':   return 'PDF';
      case 'ppt':   return 'PowerPoint';
      case 'video': return 'Vidéo';
      case 'doc':   return 'Word';
      case 'image': return 'Image';
      default:      return 'Document';
    }
  };

  /**
   * Gestionnaire de téléchargement avec enregistrement d'historique
   */
  const handleDownload = async () => {
    if (!fileInfo?.downloadUrl) return;
    
    // Enregistrer l'action de téléchargement dans l'historique
    await recordDownloadHistory(course.id);
    
    // Déclencher le téléchargement
    const link = document.createElement('a');
    link.href = fileInfo.downloadUrl;
    link.download = fileInfo.name || 'fichier';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderContent = () => {
    if (!fileInfo) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ErrorIcon sx={{ fontSize: 64, color: '#F44336', mb: 2 }} />
          <Typography variant="h6" gutterBottom>Aucun contenu disponible</Typography>
          <Button variant="contained" onClick={onClose} sx={{ mt: 2 }}>Fermer</Button>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
            <AlertTitle>Erreur d'affichage</AlertTitle>
            {errorMessage}
          </Alert>
          {fileInfo?.downloadUrl && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vous pouvez télécharger le fichier pour le consulter localement.
              </Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
                Télécharger le fichier
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    // ── PDF ──  utilise <object> — fonctionne nativement avec une blob URL
    if (fileInfo.type === 'pdf') {
      return (
        <Box sx={{ height: '100%', bgcolor: '#525659' }}>
          <object
            data={fileInfo.displayUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ display: 'block', height: '100%' }}
          >
            <Box sx={{ p: 4, textAlign: 'center', color: '#fff' }}>
              <Typography variant="body1" gutterBottom>
                Votre navigateur ne peut pas afficher ce PDF directement.
              </Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
                Télécharger le PDF
              </Button>
            </Box>
          </object>
        </Box>
      );
    }

    if (fileInfo.type === 'doc' && fileInfo.displayUrl === 'srcdoc' && docHtmlContent) {
      const srcdoc = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8"/>
          <style>
            * { box-sizing: border-box; }
            body {
              font-family: 'Calibri', 'Segoe UI', Arial, sans-serif;
              font-size: 11pt;
              line-height: 1.6;
              color: #222;
              background: #fff;
              max-width: 210mm;
              margin: 0 auto;
              padding: 15mm 20mm;
            }
            h1,h2,h3,h4,h5,h6 { color: #1a1a1a; margin-top: 1.2em; }
            table { border-collapse: collapse; width: 100%; margin: 1em 0; }
            td, th { border: 1px solid #ccc; padding: 6px 10px; }
            th { background: #f0f0f0; font-weight: bold; }
            img { max-width: 100%; height: auto; }
            p { margin: 0.4em 0; }
            ul, ol { padding-left: 1.5em; }
          </style>
        </head>
        <body>${docHtmlContent}</body>
        </html>
      `;
      return (
        <Box sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
          <iframe
            srcDoc={srcdoc}
            title={fileInfo.name}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        </Box>
      );
    }

    // ── DOCX / PPT avec Office Viewer (URL publique) ──
    if ((fileInfo.type === 'doc' || fileInfo.type === 'ppt') && fileInfo.displayUrl && fileInfo.displayUrl !== 'srcdoc') {
      return (
        <Box sx={{ height: '100%' }}>
          <iframe
            src={fileInfo.displayUrl}
            title={course?.title || 'Document'}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            allowFullScreen
          />
        </Box>
      );
    }

    // ── Vidéo ── avec gestion améliorée pour WMV
    if (fileInfo.type === 'video') {
      // Si le format n'est pas supporté, afficher un message avec option de téléchargement
      if (!fileInfo.isFormatSupported) {
        return (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <VideoIcon sx={{ fontSize: 64, color: '#90A4AE', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Format vidéo non supporté</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Le format {fileInfo.extension?.toUpperCase()} n'est pas supporté par votre navigateur pour la lecture en ligne.
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <AlertTitle>Solutions :</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li>Téléchargez la vidéo et lisez-la avec VLC, Windows Media Player ou un autre lecteur compatible</li>
                <li>Utilisez un navigateur comme Microsoft Edge ou Safari qui supporte mieux les formats Windows Media</li>
                <li>Contactez l'administrateur pour obtenir une version en MP4</li>
              </ul>
            </Alert>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} size="large">
              Télécharger la vidéo
            </Button>
          </Box>
        );
      }
      
      // Lecture vidéo avec gestion des erreurs
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}>
          <video 
            ref={videoRef}
            controls 
            autoPlay 
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              console.error('Erreur de lecture vidéo:', e);
              setError(true);
              setErrorMessage('Impossible de lire cette vidéo. Le format pourrait ne pas être supporté par votre navigateur.');
            }}
          >
            <source src={fileInfo.displayUrl} type={fileInfo.mimeType} />
            <source src={fileInfo.displayUrl} type="video/mp4" />
            <source src={fileInfo.displayUrl} type="video/webm" />
            <p>
              Votre navigateur ne supporte pas la lecture de vidéos.
              <br />
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
                Télécharger la vidéo
              </Button>
            </p>
          </video>
        </Box>
      );
    }

    // ── Image ──
    if (fileInfo.type === 'image') {
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
          <img
            src={fileInfo.displayUrl}
            alt={course?.title}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
      );
    }

    // ── Fallback générique ──
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <FilePresentIcon sx={{ fontSize: 64, color: '#90A4AE', mb: 2 }} />
        <Typography variant="h6" gutterBottom>Aperçu non disponible pour ce type de fichier</Typography>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
          Télécharger le fichier
        </Button>
      </Box>
    );
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh', borderRadius: 2, overflow: 'hidden' },
      }}
    >
      {/* ── Header ── */}
      <AppBar position="static" color="default" elevation={1} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
            {fileInfo && getFileIcon(fileInfo.type)}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight="700" noWrap>
                {course?.title || 'Cours'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {course?.matiere_nom}{fileInfo ? ` • ${getFileTypeLabel(fileInfo.type)}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {fileInfo && <Chip label={getFileTypeLabel(fileInfo.type)} size="small" variant="outlined" />}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!fileInfo?.downloadUrl}
            >
              Télécharger
            </Button>
            <IconButton onClick={onClose} edge="end" size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Contenu ── */}
      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          height: 'calc(90vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
            <CircularProgress size={48} sx={{ color: '#1976D2' }} />
            <Typography variant="body2" color="text.secondary">Chargement du fichier…</Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'hidden', height: '100%' }}>
            {renderContent()}
          </Box>
        )}
      </DialogContent>

      {/* Snackbar pour les notifications de téléchargement */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CourseViewer;